import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import jwtDecode from "jwt-decode"
import { useTranslation } from "react-i18next"

import { PublicLayout } from "layouts/Public"
import { privateRoutes } from "router/routes"

import { useAuth } from "hooks/useAuth"
import { FormNewApp, FormSignin } from "./Partials"

import "./Auth.scoped.scss"
import { useSubdomain } from "hooks/useSubdomain"
import { http } from "services/http"
import {
	sessionStorageService,
	localStorageService,
	HISTORY_PRESAVED_BIKES,
	HISTORY_SAVED_BIKES,
} from "services/browserStorage"
import { Markdown } from "components/Elements/Markdown"
import { EmailIcon, PhoneIcon } from "components/Elements/Icons"
import { BC_OPERATOR_NAME, MC_OPERATOR_NAME, MFC_OPERATOR_NAME } from "utils/constants"

export const SigninPage = () => {
	const { login, isLoggedIn } = useAuth()
	const { state, search } = useLocation()
	const { t } = useTranslation()
	const { currentSubdomain } = useSubdomain()
	const navigate = useNavigate()

	const IS_BICYCODE_SUBDOMAIN = currentSubdomain?.name === BC_OPERATOR_NAME
	const IS_MOUSTACHE_SUBDOMAIN = currentSubdomain?.name === MC_OPERATOR_NAME
	const IS_MFC_SUBDOMAIN = currentSubdomain?.name === MFC_OPERATOR_NAME
	const IS_PRO_BICYCODE_SUBDOMAIN =
		currentSubdomain?.name === BC_OPERATOR_NAME && currentSubdomain?.registrationWithG8

	React.useEffect(() => {
		if (isLoggedIn) {
			const urlParams = new URLSearchParams(search)
			const backendAccessToken = urlParams.get("backend_access_token")
			if (backendAccessToken) {
				initSupportSession(backendAccessToken)
			} else {
				navigate(state?.from || privateRoutes.home.path)
				// Remove old localStorage for history page
				localStorageService.remove(HISTORY_SAVED_BIKES)
				localStorageService.remove(HISTORY_PRESAVED_BIKES)
			}
		}
	}, [isLoggedIn]) // eslint-disable-line

	const initSupportSession = async (backendAccessToken) => {
		const decodedBackendAccessToken = jwtDecode(backendAccessToken)
		// init session in storage
		sessionStorageService.initSession({
			entity_id: decodedBackendAccessToken?.entity_id,
			user_id: decodedBackendAccessToken?.user_id,
		})
		// save token in API config (this will save token in storage)
		http.saveConfig(backendAccessToken)
		// New login with the backendAccessToken (this will save user and entity in storage)
		// NB: the useEffect of the signin page is loaded before the useAuth useEffect
		await login(null, null, true)
		navigate(privateRoutes.searchBike.path)
	}

	return (
		<PublicLayout className="login-page" showFooter={true}>
			<div className="l-grid">
				<div className="lg:w-1/2 w-full flex">
					<div className="c-card u-br-xl p-5 lg:px-12 lg:py-7 l-form-width mx-auto mb-7">
						<h1
							className="c-h1 text-xl font-semibold text-primary text-center mb-7"
							style={{ lineHeight: 1.4 }}
						>
							{IS_PRO_BICYCODE_SUBDOMAIN
								? t("pro_title", { ns: "auth" })
								: t("title", { ns: "auth" })}
						</h1>
						<FormSignin currentSubdomain={currentSubdomain} />
					</div>
				</div>

				<div
					className={`c-signin-app lg:w-1/2 w-full ${
						IS_PRO_BICYCODE_SUBDOMAIN ? "text-center" : ""
					}`}
				>
					{!IS_MOUSTACHE_SUBDOMAIN && !IS_PRO_BICYCODE_SUBDOMAIN && (
						<div
							className={
								"c-card text-white text-s l-form-width--l bg-primary u-br-xl p-12 mb-7 mx-auto " +
								(currentSubdomain?.id === "mfc" ? "c-card--red" : "")
							}
						>
							<Markdown
								specificComponents={{
									h1: ({ children }) => (
										<h1 className="c-h1 text-xl font-semibold uppercase mb-5">
											{children}
										</h1>
									),
									p: ({ children, node }) =>
										node.position.start.line === 4 ? (
											<p>{children}</p>
										) : (
											<p className="mb-5">{children}</p>
										),
								}}
							>
								{t("news", { ns: "auth" })}
							</Markdown>

							<FormNewApp />

							{(IS_BICYCODE_SUBDOMAIN || IS_MFC_SUBDOMAIN) && (
								<p className="mt-5 text-center">
									{t("contact", { ns: "auth" })}
									{IS_BICYCODE_SUBDOMAIN && (
										<> pro@bicycode.eu ou asso@bicycode.eu</>
									)}
									{IS_MFC_SUBDOMAIN && <> support.mfc@o-code.co</>}
								</p>
							)}
						</div>
					)}

					{IS_PRO_BICYCODE_SUBDOMAIN && (
						<div
							className="c-card u-left text-white text-s bg-primary u-br-xl p-12 mb-7 mx-auto inline-block"
							style={{ maxWidth: 540 }}
						>
							<h1 className="c-h1 text-l l-form-width--l font-semibold uppercase mb-5">
								{t("G8.title", { ns: "auth" })}
							</h1>
							<div className="flex justify-between">
								<img
									src="./G8.jpeg"
									alt="G8"
									className="mr-7"
									style={{ maxWidth: "135px" }}
								/>
								<div>
									<Markdown
										specificComponents={{
											p: ({ children }) => <p className="mb-2">{children}</p>,
										}}
									>
										{t("G8.paragraph", { ns: "auth" })}
									</Markdown>
									<div className="flex items-center mb-2">
										<EmailIcon size={18} />
										<p className="ml-2">{t("G8.mail", { ns: "auth" })}</p>
									</div>
									<div className="flex items-center">
										<PhoneIcon size={18} />
										<p className="ml-2">{t("G8.phone", { ns: "auth" })}</p>
									</div>
								</div>
							</div>
						</div>
					)}

					{IS_MOUSTACHE_SUBDOMAIN && (
						<div
							className="c-card u-left text-white text-s bg-primary u-br-xl p-12 mb-7 mx-auto inline-block"
							style={{ maxWidth: 540 }}
						>
							<h1 className="c-h1 text-l l-form-width--l font-semibold uppercase mb-3">
								{t("moustache.title", { ns: "auth" })} :
							</h1>
							<Markdown
								specificComponents={{
									p: ({ children }) => <p className="mb-6">{children}</p>,
								}}
							>
								{t("moustache.alreadyBCAccount", { ns: "auth" })}
							</Markdown>
							<h1 className="c-h1 text-l l-form-width--l font-semibold uppercase mb-3">
								{t("moustache.title2", { ns: "auth" })} :
							</h1>
							<Markdown
								specificComponents={{
									p: ({ children }) => <p className="mb-6">{children}</p>,
								}}
							>
								{t("moustache.noBCAccount", { ns: "auth" })}
							</Markdown>
							<Markdown
								specificComponents={{
									p: ({ children }) => <p className="mb-2">{children}</p>,
								}}
							>
								{t("moustache.ifProblem", { ns: "auth" })}
							</Markdown>
						</div>
					)}
				</div>
			</div>
		</PublicLayout>
	)
}
